import OutlineButton from "@components/Buttons/OutlineButton";
import { MinusIcon, UnMatchIcon } from "@components/Icons";
import { useTheme } from "@hooks/useTheme";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import GroupedAccordion from "./GroupedAccordion";
import useEscapeKey from "@hooks/useEscapeKey";

const SbomItemsListVerificationModal = ({
  title = "SBOM ITEM LIST",
  missings = [],
  notApplicables = [],
  itemsPerPage = 5,
  firstColumnTitle = "",
  secondColumnTitle = "",
  scanComponents = [],
  isOpen = false,
  onClose,
  "data-testid": dataTestId = "sbom-items-list-verification-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // If modal is closed, don't render
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-center backdrop-blur-[1px] p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`${colors.bgModal} max-h-[90vh] w-[90vw] max-w-[1400px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {title || t("project.sbom.item-list-title", "SBOM ITEM LIST")}
            </h2>
            <button
              onClick={onClose}
              className={`${colors.textSecondary} transition-colors duration-200 hover:${colors.textPrimary}`}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Section - Scrollable */}
        <div className="flex flex-1 flex-col px-6 py-4 min-h-0 overflow-y-auto space-y-4" data-testid={`${dataTestId}-content`}>
          <div
            className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgTertiary} p-6 space-y-6 shadow-[0px_20px_40px_rgba(0,0,0,0.25)]`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Missing List */}
              <div className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary} p-4 space-y-4`} data-testid={fieldContainerTestId("missing-list")}>
                <div className="flex items-center gap-2">
                  <UnMatchIcon size={32} />
                  <h3 className={`text-base font-semibold tracking-wide ${colors.textSubTitle}`} data-testid={fieldValueTestId("missing-list-title")}>
                    {firstColumnTitle}
                  </h3>
                </div>
                <GroupedAccordion list={missings} itemsPerPage={itemsPerPage} scanComponents={scanComponents} />
              </div>

              {/* Not Applicable List */}
              <div className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary} p-4 space-y-4`} data-testid={fieldContainerTestId("not-applicable-list")}>
                <div className="flex items-center gap-2">
                  <MinusIcon size={32} />
                  <h3 className={`text-base font-semibold tracking-wide ${colors.textSubTitle}`} data-testid={fieldValueTestId("not-applicable-list-title")}>
                    {secondColumnTitle}
                  </h3>
                </div>
                <GroupedAccordion list={notApplicables} itemsPerPage={itemsPerPage} scanComponents={scanComponents} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <OutlineButton
              data-testid={`${dataTestId}-close-button`}
              onClick={onClose}
              size="md"
            >
              {t("common.close-button")}
            </OutlineButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SbomItemsListVerificationModal;
